import { useEdicaoDeGrade } from '@/lib/edicao-pendente';
import { useEffect, useMemo, useRef } from 'react';
import {
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  EventApiModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  TextEditorModule,
  TooltipModule,
  themeQuartz,
  type CellFocusedEvent,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type ValueSetterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ROTULO_CENTRO_TIPO_NO, ROTULO_CPM_ORIGEM, type CentroGradeResponse } from '@triade/contracts';
import { formatarDataHora, formatarMoedaCompleta, formatarNumeroCompleto, formatarTextoDigitado } from '@/lib/format';
import { MODULOS_VALIDACAO } from '@/lib/ag-grid-validacao';
import { cn } from '@/lib/utils';
import { cpmDigitado, cpmParaTexto, legendaCelula, minutosDigitado, minutosParaTexto, rotuloCompetencia, semZeros } from './tipos';

// So modulos Community, cada um explicito (nada de AllCommunityModule).
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  RowApiModule,
  RenderApiModule,
  CellApiModule,
  ColumnApiModule,
  EventApiModule,
  TextEditorModule,
  CellStyleModule,
  TooltipModule,
  ...MODULOS_VALIDACAO,
]);

const TEMA = themeQuartz.withParams({
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  headerFontSize: 12,
  headerFontWeight: 500,
  headerTextColor: 'var(--ink-muted)',
  headerBackgroundColor: 'var(--surface-muted)',
  backgroundColor: 'var(--card)',
  foregroundColor: 'var(--ink-secondary)',
  borderColor: 'var(--border)',
  accentColor: 'var(--primary)',
  rowHoverColor: 'var(--surface-muted)',
  rowHeight: 46,
  headerHeight: 36,
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  cellHorizontalPadding: 12,
  inputFocusBorder: { color: 'var(--primary)' },
  inputFocusShadow: { color: 'rgba(37,99,235,0.12)', spread: 3, radius: 0 },
});

/** Chave de uma alteracao pendente: o mes de um centro, ou os minutos do centro (`competencia` = 'minutos'). */
export const chaveCelula = (centroId: string, competencia: string) => `${centroId}|${competencia}`;
export const COL_MINUTOS = 'minutos';

interface Contexto {
  editaCpm: boolean;
  editaMinutos: boolean;
  /** Alteracoes pendentes: chave -> texto digitado. */
  edicoes: ReadonlyMap<string, string>;
  editar: (centro: CentroGradeResponse, coluna: string, texto: string) => void;
}
const ctx = (p: { context: unknown }) => p.context as Contexto;

function valido(coluna: string, texto: string): boolean {
  return (coluna === COL_MINUTOS ? minutosDigitado(texto) : cpmDigitado(texto)) !== null;
}

function Centro(p: ICellRendererParams<CentroGradeResponse>) {
  const c = p.data;
  if (!c) return null;
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="flex items-center gap-2">
        <span className="tabular shrink-0 font-medium text-ink">{c.caminho ?? c.codigo}</span>
        <span className="truncate font-medium text-ink">{c.nome}</span>
      </span>
      <span className="truncate text-caption text-ink-muted">{ROTULO_CENTRO_TIPO_NO[c.tipoNo ?? 'ANALITICO']}{c.ativo ? '' : ' · inativo'}</span>
    </span>
  );
}

function CelulaMes(p: ICellRendererParams<CentroGradeResponse>) {
  const c = p.data;
  const col = p.column?.getColId() ?? '';
  if (!c) return null;
  const pendente = ctx(p).edicoes.get(chaveCelula(c.id, col));
  if (pendente !== undefined) return <span className="tabular font-semibold">{formatarTextoDigitado(pendente, 'custo')}</span>;
  const cel = c.celulas?.[col];
  if (!cel || cel.valor === undefined) return <span className="text-ink-disabled">—</span>;
  const legenda = legendaCelula(cel);
  return (
    <span className="flex flex-col items-end leading-tight">
      <span className="tabular font-semibold text-ink">{cpmParaTexto(cel.valor)}</span>
      {legenda && <span className="text-[11px] text-ink-muted">{legenda}</span>}
    </span>
  );
}

function colunas(meses: string[], comCpm: boolean): ColDef<CentroGradeResponse>[] {
  const direita = { headerClass: 'ag-right-aligned-header', cellClass: 'ag-right-aligned-cell tabular' } as const;
  const base: ColDef<CentroGradeResponse>[] = [
    { colId: 'centro', headerName: 'Centro', minWidth: 230, flex: comCpm ? undefined : 1, width: 230, pinned: 'left', suppressNavigable: true, cellRenderer: Centro },
    {
      colId: COL_MINUTOS,
      headerName: 'Min. disponíveis/mês',
      width: 150,
      editable: (p) => ctx(p).editaMinutos,
      valueGetter: (p) => {
        const c = p.data;
        if (!c) return '';
        return ctx(p).edicoes.get(chaveCelula(c.id, COL_MINUTOS)) ?? minutosParaTexto(c.minutosDisponiveisMes);
      },
      valueSetter: (p: ValueSetterParams<CentroGradeResponse>) => {
        if (p.data) ctx(p).editar(p.data, COL_MINUTOS, p.newValue == null ? '' : String(p.newValue));
        return false; // o dado vive no estado da tela
      },
      tooltip: (p) => {
        const c = p.data;
        if (!c) return undefined;
        const e = ctx(p).edicoes.get(chaveCelula(c.id, COL_MINUTOS));
        if (e !== undefined && !valido(COL_MINUTOS, e)) return 'Valor inválido: número maior ou igual a zero, com até 6 casas';
        return e !== undefined ? 'Alteração ainda não salva' : 'Minutos disponíveis por mês (cadastro do centro)';
      },
      ...direita,
      cellClass: (p) => {
        const c = p.data;
        const e = c ? ctx(p).edicoes.get(chaveCelula(c.id, COL_MINUTOS)) : undefined;
        return cn('ag-right-aligned-cell tabular', e !== undefined && (valido(COL_MINUTOS, e) ? 'bg-warning-bg text-ink' : 'bg-danger-bg text-danger-ink'));
      },
    },
  ];
  if (!comCpm) return base;
  return [
    ...base,
    ...meses.map<ColDef<CentroGradeResponse>>((m) => ({
      colId: m,
      headerName: rotuloCompetencia(m),
      width: 112,
      editable: (p) => ctx(p).editaCpm && (p.data?.ativo ?? false),
      valueGetter: (p) => {
        const c = p.data;
        if (!c) return '';
        const e = ctx(p).edicoes.get(chaveCelula(c.id, m));
        if (e !== undefined) return e;
        const v = c.celulas?.[m]?.valor;
        return v === undefined ? '' : formatarNumeroCompleto(v); // valor completo (o editor mostra tudo); a celula exibe nas casas da empresa
      },
      valueSetter: (p: ValueSetterParams<CentroGradeResponse>) => {
        if (p.data) ctx(p).editar(p.data, m, p.newValue == null ? '' : String(p.newValue));
        return false;
      },
      cellRenderer: CelulaMes,
      tooltip: (p) => {
        const c = p.data;
        if (!c) return undefined;
        const e = ctx(p).edicoes.get(chaveCelula(c.id, m));
        if (e !== undefined && !valido(m, e)) return 'Valor inválido: R$/min maior ou igual a zero, com até 10 casas';
        if (e !== undefined) return 'Alteração ainda não salva (vira uma nova versão do mês)';
        const cel = c.celulas?.[m];
        if (!cel || cel.valor === undefined) return c.ativo ? 'Sem CPM neste mês: digite para registrar' : 'Centro inativo';
        return `${formatarMoedaCompleta(cel.valor)}/min · origem ${ROTULO_CPM_ORIGEM[cel.origem]} · versão ${cel.versao}${cel.criadoPor ? ` · ${cel.criadoPor}` : ''} · ${formatarDataHora(cel.criadoEm)}${cel.motivo ? ` · ${cel.motivo}` : ''}`;
      },
      ...direita,
      cellClass: (p) => {
        const c = p.data;
        const e = c ? ctx(p).edicoes.get(chaveCelula(c.id, m)) : undefined;
        return cn('ag-right-aligned-cell tabular', e !== undefined && (valido(m, e) ? 'bg-warning-bg text-ink' : 'bg-danger-bg text-danger-ink'), !(c?.ativo ?? true) && 'opacity-60');
      },
    })),
  ];
}

export interface GradeCpmProps {
  centros: CentroGradeResponse[];
  meses: string[];
  /** cpm.ver: mostra os meses (sem ela a API nem envia R$ e a grade so tem os minutos). */
  comCpm: boolean;
  editaCpm: boolean;
  editaMinutos: boolean;
  edicoes: ReadonlyMap<string, string>;
  onEditar: (centro: CentroGradeResponse, coluna: string, texto: string) => void;
  /** Celula de um mes ganhou o foco (liga o botao de versoes). */
  onFoco?: (centro: CentroGradeResponse | null, competencia: string | null) => void;
  apiRef?: React.MutableRefObject<GridApi<CentroGradeResponse> | null>;
}

/**
 * Grade centro x mes (AG Grid Community). Edicao tipo planilha: setas, Tab e Enter navegam; digitar substitui; Enter confirma e
 * desce. Nada e calculado aqui: o valor de cada mes e o que a API devolveu (string decimal com 10 casas) e a edicao e so texto
 * ate salvar; corrigir um mes grava a proxima versao (append-only).
 */
export function GradeCpm({ centros, meses, comCpm, editaCpm, editaMinutos, edicoes, onEditar, onFoco, apiRef }: GradeCpmProps) {
  const api = useRef<GridApi<CentroGradeResponse> | null>(null);
  const edicaoGrade = useEdicaoDeGrade();
  const aoEditarRef = useRef(onEditar);
  aoEditarRef.current = onEditar;
  const aoFocoRef = useRef(onFoco);
  aoFocoRef.current = onFoco;

  const contexto = useMemo<Contexto>(() => ({ editaCpm, editaMinutos, edicoes, editar: (c, col, t) => aoEditarRef.current(c, col, t) }), [editaCpm, editaMinutos, edicoes]);
  const defs = useMemo(() => colunas(meses, comCpm), [meses, comCpm]);

  useEffect(() => {
    api.current?.refreshCells({ force: true });
  }, [edicoes, centros]);

  const aoPronta = (e: GridReadyEvent<CentroGradeResponse>) => {
    api.current = e.api;
    if (apiRef) apiRef.current = e.api;
  };

  const aoFocar = (e: CellFocusedEvent<CentroGradeResponse>) => {
    const col = typeof e.column === 'object' && e.column ? (e.column as { getColId(): string }).getColId() : null;
    const linha = e.rowIndex === null ? undefined : e.api.getDisplayedRowAtIndex(e.rowIndex)?.data;
    aoFocoRef.current?.(linha ?? null, col && /^\d{4}-\d{2}$/.test(col) ? col : null);
  };

  return (
    <div data-testid="grade-cpm" className="h-[460px] w-full [&_.ag-cell]:flex [&_.ag-cell]:items-center [&_.ag-right-aligned-cell]:justify-end">
      <AgGridReact<CentroGradeResponse>
        theme={TEMA}
        rowData={centros}
        columnDefs={defs}
        context={contexto}
        getRowId={(p) => p.data.id}
        onGridReady={aoPronta}
        {...edicaoGrade}
        onCellFocused={aoFocar}
        suppressColumnVirtualisation
        enterNavigatesVertically
        enterNavigatesVerticallyAfterEdit
        stopEditingWhenCellsLoseFocus
        suppressMovableColumns
        tooltipShowDelay={400}
        noRowsOverlayComponent={() => null}
        defaultColDef={{ sortable: false, resizable: false, suppressHeaderMenuButton: true }}
      />
    </div>
  );
}
